import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	iCurrentTerms,
	iTermsContent,
	TermsStatusEnum,
	TermsUnavailableReasonEnum,
	TermsViewerModeEnum,
} from "../../types/termsTypes";
import {
	acceptTermsThunk,
	fetchCurrentTermsThunk,
	fetchTermsContentThunk,
	fetchTermsVersionThunk,
	remindSuperAdminsThunk,
} from "./termsThunk";

interface iTermsState {
	current: iCurrentTerms | null;
	/** Set when the gate could not be evaluated. Non-blocking — see the reducer. */
	unavailableReason: TermsUnavailableReasonEnum | null;
	isLoadingCurrent: boolean;
	/** True once `current` has resolved or failed at least once, so surfaces don't flash on first paint. */
	hasResolved: boolean;

	content: iTermsContent | null;
	isLoadingContent: boolean;
	contentError: string | null;

	/**
	 * The viewed version's own effective date — distinct from
	 * `current.effectiveDate`, which is the *live* published version's date and
	 * does not apply once the read-only viewer is showing a superseded
	 * (already-accepted) version.
	 */
	viewedVersionEffectiveDate: string | null;

	viewerMode: TermsViewerModeEnum | null;
	isAccepting: boolean;
	acceptError: string | null;

	cooldownExpiresAt: string | null;
	reminderRecipientName: string | null;
	isReminding: boolean;
	reminderError: string | null;
}

const initialState: iTermsState = {
	current: null,
	unavailableReason: null,
	isLoadingCurrent: false,
	hasResolved: false,

	content: null,
	isLoadingContent: false,
	contentError: null,
	viewedVersionEffectiveDate: null,

	viewerMode: null,
	isAccepting: false,
	acceptError: null,

	cooldownExpiresAt: null,
	reminderRecipientName: null,
	isReminding: false,
	reminderError: null,
};

const termsSlice = createSlice({
	name: "terms",
	initialState,
	reducers: {
		openTermsViewer: (state, action: PayloadAction<TermsViewerModeEnum>) => {
			state.viewerMode = action.payload;
			state.acceptError = null;
		},
		closeTermsViewer: (state) => {
			// Closing without accepting records nothing — during the notice period
			// the ✕ *is* "not now".
			state.viewerMode = null;
			state.contentError = null;
			state.acceptError = null;
			state.viewedVersionEffectiveDate = null;
		},
		/**
		 * Opened by the 403 interceptor when the gate fires mid-session. Only
		 * flips the status; the next `current` fetch fills in the detail.
		 */
		openTermsGate: (state, action: PayloadAction<{ version: string; effectiveAt: string }>) => {
			state.unavailableReason = null;
			if (state.current) {
				state.current = {
					...state.current,
					status: TermsStatusEnum.BLOCKED,
					version: action.payload.version,
					effectiveDate: action.payload.effectiveAt,
				};
			}
		},
		resetTerms: () => initialState,
	},

	extraReducers: (builder) => {
		builder.addCase(fetchCurrentTermsThunk.pending, (state) => {
			state.isLoadingCurrent = true;
		});
		builder.addCase(fetchCurrentTermsThunk.fulfilled, (state, action) => {
			state.isLoadingCurrent = false;
			state.hasResolved = true;
			state.unavailableReason = null;
			state.current = action.payload;

			// Rehydrate the reminder from the server so a new tab, a refresh or a
			// colleague's browser all agree on whether one is outstanding. The
			// cooldown is per vendor, so this is the only correct source.
			//
			// `undefined` means the field is absent — Baqaal does not send it yet —
			// so leave whatever this session already knows. `null` is the server
			// actively saying the cooldown has lapsed, and does clear it.
			if (action.payload.reminderCooldownExpiresAt !== undefined) {
				state.cooldownExpiresAt = action.payload.reminderCooldownExpiresAt;
				state.reminderRecipientName = action.payload.reminderCooldownExpiresAt
					? (action.payload.superAdminName ?? state.reminderRecipientName)
					: null;
			}
		});
		builder.addCase(fetchCurrentTermsThunk.rejected, (state, action) => {
			state.isLoadingCurrent = false;
			state.hasResolved = true;
			// Fail open. `current` stays null, so `status` is undefined and every
			// surface — which renders on an explicit 'notice' or 'blocked' — stays
			// hidden. Do not invent a status here.
			state.current = null;
			state.unavailableReason = action.payload?.reason ?? null;
		});

		builder.addCase(fetchTermsContentThunk.pending, (state) => {
			state.isLoadingContent = true;
			state.contentError = null;
		});
		builder.addCase(fetchTermsContentThunk.fulfilled, (state, action) => {
			state.isLoadingContent = false;
			state.content = action.payload;
		});
		builder.addCase(fetchTermsContentThunk.rejected, (state, action) => {
			state.isLoadingContent = false;
			state.content = null;
			state.contentError = action.payload?.message ?? "Could not load the terms";
		});

		builder.addCase(fetchTermsVersionThunk.fulfilled, (state, action) => {
			state.viewedVersionEffectiveDate = action.payload.effectiveDate;
		});
		builder.addCase(fetchTermsVersionThunk.rejected, (state) => {
			// Non-fatal: the document itself still renders from `content`, just
			// without the "effective" line for this open.
			state.viewedVersionEffectiveDate = null;
		});

		builder.addCase(acceptTermsThunk.pending, (state) => {
			state.isAccepting = true;
			state.acceptError = null;
		});
		builder.addCase(acceptTermsThunk.fulfilled, (state) => {
			// Deliberately does not set `status`. The response carries only
			// `{ version, acceptedAt }`; the caller refetches `current`.
			state.isAccepting = false;
			state.viewerMode = null;
		});
		builder.addCase(acceptTermsThunk.rejected, (state, action) => {
			state.isAccepting = false;
			state.acceptError = action.payload?.message ?? "Could not record your acceptance";
		});

		builder.addCase(remindSuperAdminsThunk.pending, (state) => {
			state.isReminding = true;
			state.reminderError = null;
		});
		builder.addCase(remindSuperAdminsThunk.fulfilled, (state, action) => {
			state.isReminding = false;
			state.cooldownExpiresAt = action.payload.cooldownExpiresAt;
			state.reminderRecipientName = action.payload.recipientName;
		});
		builder.addCase(remindSuperAdminsThunk.rejected, (state, action) => {
			state.isReminding = false;
			state.reminderError = action.payload?.message ?? "Could not send the reminder";
		});
	},
});

export const { openTermsViewer, closeTermsViewer, openTermsGate, resetTerms } = termsSlice.actions;

export default termsSlice.reducer;
